/**
 * Workflow naming + describing helpers — the single source of the convention
 * that a workflow is presented as three separable layers:
 *
 *   1. NAME    — a short, scannable label (the loop `title`). No arrow chains.
 *   2. SUMMARY — one plain-English sentence of what it does (`loopSummary`,
 *                derived from the loop `description`, stripped of the catalog's
 *                "Replaces:/Judge:" marketing tail).
 *   3. CHAIN   — the multi-step reality, rendered from the STEPS data
 *                (`humanizeStepName`), never crammed into the name.
 *
 * Pure string helpers so both the server read layer (`listFleetLoops`) and the
 * UI (FleetTable, Loop Studio header, <StepChain>) format identically.
 */

/**
 * `draft-customer-email` / `draft_customer_email` → `Draft customer email`.
 * Step names are authored as kebab/snake verbs; this is how a human reads them.
 */
export function humanizeStepName(name: string): string {
  const s = name.replace(/[-_]+/g, " ").trim();
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Humanize an ordered list of raw step names into chain labels. */
export function stepChainLabels(stepNames: string[]): string[] {
  return stepNames.map(humanizeStepName).filter(Boolean);
}

/**
 * The plain-English summary shown under a workflow's name. Loop descriptions
 * follow the catalog convention `<pitch> Replaces: <…>. Judge: <…>; <gate>;
 * <outcome>.` — only the leading pitch reads as a user-facing summary, so we
 * cut at the first marketing marker. Returns null when there's nothing to show.
 */
export function loopSummary(description: string | null | undefined): string | null {
  if (!description) return null;
  const cut = (description.split(/\s+(?:Replaces|Judge|Gate|Outcome):/i)[0] ?? "").trim();
  return cut.length > 0 ? cut : null;
}
